import React from 'react';

const TermsOfService = () => {
  return (
    <div className="legal-page">
      <div className="legal-container">
        <h1>Terms of Service</h1>
        <p className="last-updated">Last Updated: October 14, 2025</p>

        <section>
          <h2>1. Acceptance of Terms</h2>
          <p>
            By accessing or using Invy ("Service"), you agree to be bound by these Terms of Service ("Terms"). 
            If you do not agree to these Terms, please do not use the Service.
          </p>
          <p>
            These Terms constitute a legally binding agreement between you and Invy. We reserve the right to modify 
            these Terms at any time, and your continued use of the Service constitutes acceptance of any changes.
          </p>
        </section>

        <section>
          <h2>2. Description of Service</h2>
          <p>
            Invy is a cloud-based inventory management system that allows you to:
          </p>
          <ul>
            <li>Organize items into locations and containers hierarchically</li>
            <li>Scan barcodes and QR codes to manage inventory</li>
            <li>Automatically retrieve product information from third-party databases</li>
            <li>Store and manage product images</li>
            <li>Track quantities and descriptions of items</li>
            <li>Generate QR codes for locations and containers</li>
          </ul>
        </section>

        <section>
          <h2>3. Account Registration and Security</h2>
          <h3>3.1 Account Creation</h3>
          <ul>
            <li>You must sign in using one of our supported OAuth providers (Google, Microsoft, or Apple)</li>
            <li>You must provide accurate and complete information</li>
            <li>You must be at least 13 years of age to use the Service</li>
            <li>You are responsible for maintaining the security of your account</li>
          </ul>

          <h3>3.2 Account Security</h3>
          <ul>
            <li>You are responsible for all activities that occur under your account</li>
            <li>You must notify us immediately of any unauthorized access</li>
            <li>We are not liable for any loss or damage from unauthorized account access</li>
            <li>Do not share your account credentials with others</li>
          </ul>
        </section>

        <section>
          <h2>4. Acceptable Use Policy</h2>
          <h3>4.1 You Agree NOT To:</h3>
          <ul>
            <li>Use the Service for any illegal purpose or in violation of any laws</li>
            <li>Attempt to gain unauthorized access to the Service or other users' accounts</li>
            <li>Interfere with or disrupt the Service or servers</li>
            <li>Use automated tools to access the Service without permission</li>
            <li>Upload malicious code, viruses, or harmful software</li>
            <li>Impersonate another person or entity</li>
            <li>Harass, abuse, or harm other users</li>
            <li>Collect or harvest user information without consent</li>
            <li>Use the Service to store or distribute illegal content</li>
            <li>Reverse engineer or attempt to extract source code</li>
          </ul>

          <h3>4.2 Rate Limits</h3>
          <ul>
            <li>We impose rate limits to ensure fair usage (100 requests per 15 minutes)</li>
            <li>Exceeding rate limits may result in temporary suspension</li>
            <li>Contact us if you need higher limits for legitimate use</li>
          </ul>
        </section>

        <section>
          <h2>5. User Content and Data</h2>
          <h3>5.1 Your Content</h3>
          <ul>
            <li>You retain all rights to the inventory data you create</li>
            <li>You grant us a license to store and process your data to provide the Service</li>
            <li>You are responsible for the accuracy of your data</li>
            <li>You must have the right to upload any images or content</li>
          </ul>

          <h3>5.2 Prohibited Content</h3>
          <p>You may not upload content that:</p>
          <ul>
            <li>Violates any intellectual property rights</li>
            <li>Contains personal information of others without consent</li>
            <li>Is illegal, harmful, threatening, abusive, or obscene</li>
            <li>Infringes on privacy or publicity rights</li>
          </ul>

          <h3>5.3 Data Backup</h3>
          <ul>
            <li>While we maintain regular backups, you are responsible for maintaining your own backups</li>
            <li>We are not liable for any data loss</li>
            <li>You can export your data at any time</li>
          </ul>
        </section>

        <section>
          <h2>6. Third-Party Services</h2>
          <h3>6.1 OAuth Providers</h3>
          <ul>
            <li>We use Google, Microsoft, and Apple for authentication</li>
            <li>Your use of these services is subject to their respective terms and policies</li>
            <li>We are not responsible for the practices of these providers</li>
          </ul>

          <h3>6.2 UPC Item DB</h3>
          <ul>
            <li>We use UPC Item DB API to retrieve product information</li>
            <li>Product data accuracy is not guaranteed</li>
            <li>Some products may not be found in their database</li>
            <li>You can disable automatic lookups if preferred</li>
          </ul>

          <h3>6.3 Cloudflare Services</h3>
          <ul>
            <li>The Service is hosted on Cloudflare infrastructure</li>
            <li>Cloudflare's terms and policies also apply</li>
          </ul>
        </section>

        <section>
          <h2>7. Intellectual Property</h2>
          <h3>7.1 Our Rights</h3>
          <ul>
            <li>Invy and all related trademarks, logos, and service marks are owned by us</li>
            <li>The Service's code, design, and functionality are protected by copyright</li>
            <li>You may not copy, modify, or redistribute the Service</li>
          </ul>

          <h3>7.2 License to Use</h3>
          <ul>
            <li>We grant you a limited, non-exclusive, non-transferable license to use the Service</li>
            <li>This license is revocable and terminates when you stop using the Service</li>
            <li>You may not sublicense or transfer this license</li>
          </ul>
        </section>

        <section>
          <h2>8. Service Availability and Modifications</h2>
          <h3>8.1 Service Availability</h3>
          <ul>
            <li>We strive for 99.9% uptime but do not guarantee uninterrupted service</li>
            <li>Planned maintenance will be announced in advance when possible</li>
            <li>We are not liable for service interruptions</li>
          </ul>

          <h3>8.2 Modifications</h3>
          <ul>
            <li>We may modify, suspend, or discontinue any aspect of the Service</li>
            <li>We may add or remove features at our discretion</li>
            <li>We will provide notice of material changes when possible</li>
          </ul>
        </section>

        <section>
          <h2>9. Fees and Payment</h2>
          <h3>9.1 Current Pricing</h3>
          <ul>
            <li>The Service is currently provided free of charge</li>
            <li>We reserve the right to introduce fees in the future</li>
            <li>Existing users will receive advance notice of any pricing changes</li>
          </ul>

          <h3>9.2 Future Paid Plans</h3>
          <ul>
            <li>If we introduce paid plans, you will have the option to upgrade</li>
            <li>Free tier limitations may apply</li>
            <li>Pricing will be clearly communicated</li>
          </ul>
        </section>

        <section>
          <h2>10. Termination</h2>
          <h3>10.1 By You</h3>
          <ul>
            <li>You may terminate your account at any time by contacting us</li>
            <li>Upon termination, your data will be deleted within 30 days</li>
            <li>You may export your data before termination</li>
          </ul>

          <h3>10.2 By Us</h3>
          <p>We may suspend or terminate your account if:</p>
          <ul>
            <li>You violate these Terms</li>
            <li>You engage in fraudulent or illegal activity</li>
            <li>Your account has been inactive for 12+ months</li>
            <li>Required by law or legal authority</li>
          </ul>
          <p>We will provide notice when possible, but may terminate immediately for serious violations.</p>
        </section>

        <section>
          <h2>11. Disclaimers and Limitations of Liability</h2>
          <h3>11.1 Disclaimer of Warranties</h3>
          <p>
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, 
            EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:
          </p>
          <ul>
            <li>Warranties of merchantability or fitness for a particular purpose</li>
            <li>Warranties of accuracy, reliability, or availability</li>
            <li>Warranties that the Service will be error-free or secure</li>
          </ul>

          <h3>11.2 Limitation of Liability</h3>
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR:
          </p>
          <ul>
            <li>Any indirect, incidental, special, consequential, or punitive damages</li>
            <li>Loss of profits, revenue, data, or business opportunities</li>
            <li>Any damages arising from your use or inability to use the Service</li>
            <li>Any damages from unauthorized access to your account</li>
          </ul>
          <p>
            OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE PAST 12 MONTHS 
            (CURRENTLY $0 AS THE SERVICE IS FREE).
          </p>
        </section>

        <section>
          <h2>12. Indemnification</h2>
          <p>
            You agree to indemnify, defend, and hold harmless Invy and its officers, directors, employees, 
            and agents from any claims, liabilities, damages, losses, and expenses arising from:
          </p>
          <ul>
            <li>Your violation of these Terms</li>
            <li>Your violation of any rights of another party</li>
            <li>Your use of the Service</li>
            <li>Your content or data</li>
          </ul>
        </section>

        <section>
          <h2>13. Governing Law and Dispute Resolution</h2>
          <h3>13.1 Governing Law</h3>
          <p>
            These Terms are governed by the laws of the jurisdiction where Invy operates, 
            without regard to conflict of law principles.
          </p>

          <h3>13.2 Dispute Resolution</h3>
          <ul>
            <li>Any disputes shall first be attempted to be resolved through good faith negotiation</li>
            <li>If negotiation fails, disputes shall be resolved through binding arbitration</li>
            <li>You waive the right to participate in class action lawsuits</li>
            <li>Small claims court remains available for qualifying claims</li>
          </ul>
        </section>

        <section>
          <h2>14. Miscellaneous</h2>
          <h3>14.1 Entire Agreement</h3>
          <p>
            These Terms, along with our Privacy Policy, constitute the entire agreement between you and Invy.
          </p>

          <h3>14.2 Severability</h3>
          <p>
            If any provision of these Terms is found invalid or unenforceable, 
            the remaining provisions shall remain in full force and effect.
          </p>

          <h3>14.3 No Waiver</h3>
          <p>
            Our failure to enforce any right or provision of these Terms shall not be deemed a waiver of such right or provision.
          </p>

          <h3>14.4 Assignment</h3>
          <p>
            You may not assign or transfer these Terms without our prior written consent. 
            We may assign these Terms without restriction.
          </p>

          <h3>14.5 Force Majeure</h3>
          <p>
            We are not liable for any failure or delay in performance due to circumstances beyond our reasonable control, 
            including acts of God, natural disasters, war, terrorism, labor disputes, or internet failures.
          </p>
        </section>

        <section>
          <h2>15. Contact Information</h2>
          <p>
            If you have any questions about these Terms, please contact us:
          </p>
          <ul>
            <li><strong>Email:</strong> <a href="mailto:legal@invy.me">legal@invy.me</a></li>
            <li><strong>Support:</strong> <a href="mailto:support@invy.me">support@invy.me</a></li>
          </ul>
        </section>

        <section>
          <h2>16. Acknowledgment</h2>
          <p>
            BY USING THE SERVICE, YOU ACKNOWLEDGE THAT YOU HAVE READ THESE TERMS OF SERVICE, 
            UNDERSTAND THEM, AND AGREE TO BE BOUND BY THEM.
          </p>
        </section>
      </div>
    </div>
  );
};

export default TermsOfService;
